import { languageDisplayLabel, LanguagesEnum } from '@/api/enums';
import { client } from '@/client';
import { useNotifications } from '@/hooks/notifications.hook';
import { useAppSelector } from '@/hooks/store';
import { useToast } from '@/hooks/useToast';
import { selectCurrentAccount } from '@/store/features/account';
import { AppButton } from '@/ui/buttons';
import { FlexContainer } from '@/ui/styled-ui';
import {
  TranslationStatusBannerCloseIcon,
  TranslationStatusBannerCompleteIcon,
  TranslationStatusBannerPartialIcon,
} from '@/utils/helpers';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import styled, { keyframes } from 'styled-components';
import {
  STORAGE_KEY_TRANSLATION_IN_PROGRESS,
  TRANSLATION_FAILED_LANGUAGES_KEY,
  TRANSLATION_SOURCE_LANGUAGE_KEY,
  TRANSLATION_STORAGE_KEY,
} from './CourseTranslation';

const BannerContainer = styled(FlexContainer)<{ $hasFailures?: boolean }>`
  background: ${({ $hasFailures }) =>
    $hasFailures ? 'var(--yellow-pale)' : 'rgba(32, 157, 127, 0.15)'};
  border-radius: 19px;
  padding: 20px 30px;
  width: 100%;
  align-items: center;
  gap: 10px;
  margin-bottom: var(--default-padding);
`;

const CheckIconContainer = styled(FlexContainer)`
  width: 44px;
  height: 44px;
  min-width: 44px;
  min-height: 44px;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const partialBannerSpin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const PartialRetrySpinner = styled.span`
  display: inline-block;
  width: 33px;
  height: 33px;
  border: 3px solid var(--yellow-dark);
  border-top-color: var(--yellow-main);
  border-radius: 50%;
  animation: ${partialBannerSpin} 0.8s linear infinite;
`;

const TextContainer = styled(FlexContainer)`
  flex-direction: column;
  gap: 2px;
  flex: 1;
  align-items: flex-start;
  justify-content: center;
`;

const BannerTitle = styled.p`
  font-family: var(--font-family);
  font-weight: 600;
  font-size: 16px;
  line-height: 1.4;
  color: var(--black-main);
  margin: 0;
  white-space: pre-wrap;
`;

const BannerDescription = styled.p`
  font-family: var(--font-family);
  font-weight: 400;
  font-size: 14px;
  line-height: 1.4;
  color: var(--black-main);
  margin: 0;
  white-space: pre-wrap;
`;

const CloseButton = styled.button`
  width: 24px;
  height: 24px;
  min-width: 24px;
  min-height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  color: var(--black-main);

  &:hover {
    opacity: 0.7;
  }

  i {
    font-size: 12.6px;
  }
`;

type CourseTranslationStatusBannerProps = {
  courseId: string;
};

export const CourseTranslationStatusBanner: React.FC<
  CourseTranslationStatusBannerProps
> = ({ courseId }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const currentAccount = useAppSelector(selectCurrentAccount);
  const [isDismissed, setIsDismissed] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [failedLanguages, setFailedLanguages] = useState<string[]>([]);
  const [isRetrying, setIsRetrying] = useState(false);

  const clearStorage = () => {
    sessionStorage.removeItem(TRANSLATION_STORAGE_KEY(courseId));
    sessionStorage.removeItem(STORAGE_KEY_TRANSLATION_IN_PROGRESS(courseId));
    sessionStorage.removeItem(TRANSLATION_FAILED_LANGUAGES_KEY(courseId));
    sessionStorage.removeItem(TRANSLATION_SOURCE_LANGUAGE_KEY(courseId));
  };

  useEffect(() => {
    return () => {
      if (!window.location.pathname.includes('/content/')) {
        clearStorage();
      }
    };
  }, [courseId]);

  const { lastMessage, setNotificationParam } = useNotifications(
    client.translations.translationsNotifyUrl,
  );

  useEffect(() => {
    const shouldShow =
      sessionStorage.getItem(TRANSLATION_STORAGE_KEY(courseId)) === 'true';
    setShowBanner(shouldShow);

    const storedFailed = sessionStorage.getItem(
      TRANSLATION_FAILED_LANGUAGES_KEY(courseId),
    );
    if (storedFailed) {
      setFailedLanguages(JSON.parse(storedFailed));
    } else {
      setFailedLanguages([]);
    }
  }, [courseId]);

  useEffect(() => {
    if (isDismissed) {
      return;
    }

    const translationInProgress =
      sessionStorage.getItem(STORAGE_KEY_TRANSLATION_IN_PROGRESS(courseId)) ===
      'true';
    const translationCompleted =
      sessionStorage.getItem(TRANSLATION_STORAGE_KEY(courseId)) === 'true';
    const hasTranslationActivity =
      translationInProgress || translationCompleted;

    if (hasTranslationActivity) {
      setNotificationParam(courseId);
    }

    return () => {
      if (hasTranslationActivity) {
        setNotificationParam(undefined);
      }
    };
  }, [courseId, setNotificationParam, isDismissed]);

  useEffect(() => {
    if (!lastMessage?.data) return;

    const { event } = lastMessage.data;

    if (event === 'course-translation.finished') {
      setIsRetrying(false);
      sessionStorage.removeItem(STORAGE_KEY_TRANSLATION_IN_PROGRESS(courseId));

      const alreadySet =
        sessionStorage.getItem(TRANSLATION_STORAGE_KEY(courseId)) === 'true';

      if (!alreadySet) {
        setShowBanner(true);
        sessionStorage.setItem(TRANSLATION_STORAGE_KEY(courseId), 'true');
      }

      const {
        courseTranslation,
        quizTranslations,
        presentationTranslations,
        resourceTranslations,
      } = lastMessage.data.result;

      const failed = [
        ...(courseTranslation?.failedLanguages ?? []),
        ...(quizTranslations ?? []).flatMap(
          ({ failedLanguages }: { failedLanguages: string[] }) =>
            failedLanguages,
        ),
        ...(presentationTranslations ?? []).flatMap(
          ({ failedLanguages }: { failedLanguages: string[] }) =>
            failedLanguages,
        ),
        ...(resourceTranslations ?? []).flatMap(
          ({ failedLanguages }: { failedLanguages: string[] }) =>
            failedLanguages,
        ),
      ];

      const dedupedFailed = [...new Set(failed)];

      setFailedLanguages(dedupedFailed);

      sessionStorage.setItem(
        TRANSLATION_FAILED_LANGUAGES_KEY(courseId),
        JSON.stringify(dedupedFailed),
      );

      queryClient.invalidateQueries(['course', courseId]);

      setNotificationParam(undefined);
    } else if (event === 'course-translation.failed') {
      setIsRetrying(false);
      sessionStorage.removeItem(STORAGE_KEY_TRANSLATION_IN_PROGRESS(courseId));
      setNotificationParam(undefined);
    }
  }, [lastMessage, queryClient, courseId, setNotificationParam]);

  const handleClose = () => {
    setIsDismissed(true);
    setShowBanner(false);
    clearStorage();
  };

  const handleRetry = async () => {
    if (!currentAccount || isRetrying) return;

    const sourceLanguage = sessionStorage.getItem(
      TRANSLATION_SOURCE_LANGUAGE_KEY(courseId),
    ) as LanguagesEnum | null;

    if (!sourceLanguage) {
      toast?.error(
        t('translations.error.title'),
        t('translations.error.generic'),
      );
      return;
    }

    setIsRetrying(true);

    try {
      await client.translations.translateCourse(courseId, {
        accountId: currentAccount.id,
        sourceLanguage,
        targetLanguages: failedLanguages as LanguagesEnum[],
        translateQuizzes: true,
        translatePresentations: true,
        translateResources: true,
      });

      sessionStorage.setItem(
        STORAGE_KEY_TRANSLATION_IN_PROGRESS(courseId),
        'true',
      );
      sessionStorage.setItem(
        TRANSLATION_SOURCE_LANGUAGE_KEY(courseId),
        sourceLanguage,
      );
      setNotificationParam(courseId);
    } catch (err: any) {
      setIsRetrying(false);
      toast?.error(
        t('translations.error.title'),
        t('translations.error.generic'),
      );
    }
  };

  if (isDismissed || !showBanner) {
    return null;
  }

  const hasFailures = failedLanguages.length > 0;

  let statusIcon: React.ReactNode;
  if (!hasFailures) {
    statusIcon = <TranslationStatusBannerCompleteIcon />;
  } else if (isRetrying) {
    statusIcon = <PartialRetrySpinner />;
  } else {
    statusIcon = <TranslationStatusBannerPartialIcon />;
  }

  const failedLanguageLabels = failedLanguages
    .map(
      (lang) => languageDisplayLabel[lang as keyof typeof languageDisplayLabel],
    )
    .join(', ');

  const bannerTitle = hasFailures
    ? t('translations.status.partialTitle')
    : t('translations.status.completed');

  const bannerDescription = hasFailures
    ? t('translations.status.partialDescription', {
        languages: failedLanguageLabels,
      })
    : t('translations.status.completedDescription');

  return (
    <BannerContainer
      role="status"
      aria-live="polite"
      $hasFailures={hasFailures}
    >
      <CheckIconContainer>{statusIcon}</CheckIconContainer>
      <TextContainer>
        <BannerTitle>{bannerTitle}</BannerTitle>
        <BannerDescription>{bannerDescription}</BannerDescription>
      </TextContainer>
      {hasFailures && (
        <AppButton
          type="outlined"
          severity="no-background"
          onClick={handleRetry}
          isDisabled={isRetrying}
          label={t('translations.status.retry')}
        />
      )}
      <CloseButton
        type="button"
        onClick={handleClose}
        aria-label="Close banner"
        disabled={isRetrying}
      >
        <TranslationStatusBannerCloseIcon />
      </CloseButton>
    </BannerContainer>
  );
};
